import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GoogleReviews } from '../components/GoogleReviews';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, Heart, Sparkles, Compass, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { homeTranslations } from '../translations/home';
import { useSeo } from '../hooks/useSeo';

const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Stéphanie Ruiz",
  "url": "https://www.comprendre-qui-on-est.com/",
  "telephone": "+34608414296",
  "image": "https://www.comprendre-qui-on-est.com/logo.png",
  "priceRange": "À partir de 80 €",
  "description": "Numérologie et accompagnement individuel, transitions de vie, couple et famille. En présentiel à Ibiza et en visioconférence partout dans le monde.",
  "areaServed": { "@type": "Place", "name": "Sant Josep de sa Talaia, Ibiza, Espagne" },
  "knowsLanguage": ["fr", "es"],
  "availableLanguage": ["fr", "es"],
  "serviceType": [
    "Numérologie",
    "Accompagnement individuel",
    "Transitions de vie",
    "Accompagnement de couple",
    "Accompagnement familial"
  ],
  "availableChannel": [
    { "@type": "ServiceChannel", "name": "En présentiel à Sant Josep de sa Talaia, Ibiza" },
    { "@type": "ServiceChannel", "name": "Visioconférence (partout dans le monde)", "availableLanguage": ["fr", "es"] }
  ]
};

export const Home = () => {
  const { language } = useLanguage();
  const t = homeTranslations[language];


  const isFr = language === 'fr';
  useSeo(
    isFr ? "Stéphanie Ruiz | Numérologue à Ibiza et en visio" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Stéphanie Ruiz, numérologue et coach de vie à Ibiza. Accompagnement des transitions de vie, connaissance de soi et séances en visio partout dans le monde." : "A product of emergent.sh"
  );

  useEffect(() => {
    const id = 'ld-json-professional-service';
    if (document.getElementById(id)) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-end justify-center px-4 sm:px-6 lg:px-8 pt-20 pb-16 md:pb-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/hero-stef-ibiza.jpg)',
            backgroundPosition: '50% 50%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/60 via-gray-900/50 to-gray-900/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto w-full">
          <p className="text-sm sm:text-base md:text-lg uppercase tracking-[0.2em] text-white/90 font-light mb-4 md:mb-5" data-testid="hero-name">
            {language === 'fr' ? 'Stéphanie Ruiz – Numérologue & coach de vie' : 'Stéphanie Ruiz – Numeróloga & coach de vida'}
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-extralight text-white mb-8 md:mb-10 leading-relaxed tracking-wide">
            {t.hero.title}
          </h1>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/test" className="w-full sm:w-auto">
              <Button size="lg" data-testid="hero-test-cta" className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 h-auto shadow-2xl transition-all duration-300 hover:scale-105 font-semibold">
                {t.freeTest.button}
              </Button>
            </Link>
            <Link to="/booking" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" data-testid="hero-booking-cta" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white/70 text-white hover:bg-white/20 hover:border-white text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 h-auto shadow-xl transition-all duration-300 hover:scale-105">
                {t.hero.cta}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Professional Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-6">
            {t.professional.title}
          </h2>
          <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed text-center">
            <p>{t.professional.description}</p>
            <p>{t.professional.approach}</p>
            <p className="text-xl font-light text-teal-600">{t.professional.goal}</p>
          </div>
        </div>
      </section>

      {/* Free Test Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500 to-pink-500 p-8 sm:p-12 text-center shadow-2xl">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 text-white text-sm font-medium mb-5 backdrop-blur-sm">
                <Sparkles size={16} />
                {t.freeTest.badge}
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-4 leading-tight">
                {t.freeTest.title}
              </h2>
              <p className="text-base sm:text-lg text-white/95 leading-relaxed mb-8 max-w-2xl mx-auto font-light">
                {t.freeTest.description}
              </p>
              <Link to="/test">
                <Button size="lg" data-testid="home-test-cta" className="bg-white text-teal-600 hover:bg-teal-50 text-base sm:text-lg px-8 sm:px-10 py-6 h-auto shadow-xl transition-all duration-300 hover:scale-105">
                  {t.freeTest.button}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-16">
            {t.services.title}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Numérologie */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-6 shadow-lg">
                  <Calculator size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.services.numerology.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t.services.numerology.description}
                </p>
                <Link to="/services">
                  <Button className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white">
                    {t.services.cta}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Thérapie */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg">
                  <Heart size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.services.therapy.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t.services.therapy.description}
                </p>
                <Link to="/services">
                  <Button className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white">
                    {t.services.cta}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Coaching */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-6 shadow-lg">
                  <Compass size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.services.coaching.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t.services.coaching.description}
                </p>
                <Link to="/services">
                  <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    {t.services.cta}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Énergétique */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.services.energy.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t.services.energy.description}
                </p>
                <Link to="/services">
                  <Button className="bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white">
                    {t.services.cta}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Avis Google */}
      <GoogleReviews />

      {/* Final CTA */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-500 to-pink-600">
          <div className="absolute inset-0 bg-black/10"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
            {t.finalCta.title}
          </h2>
          <p className="text-xl text-white/95 mb-12 leading-relaxed font-light">
            {t.finalCta.subtitle}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-teal-600 text-white hover:bg-teal-700 text-xl px-14 py-10 h-auto shadow-2xl transition-all duration-300 hover:scale-105">
              {t.finalCta.button}
              <ArrowRight className="ml-3" size={24} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
